// A case's perfusion scans (ENG-7381, ENG-10008). The types follow spdata-api's serializer and the
// SP Data app's `src/features/perfusion-scan/perfusionScan.ts`, which reads the same routes.
//
// A scan the app made keeps its scanned values in `result.fields`. An older scan from the Perfusion
// Companion app keeps whatever that app sent in `result`, so `fields` is never certain, and such a
// scan is shown as its free-form payload and has nothing to apply.

/** A frame stored in S3 alongside the scan. The frame route addresses it by its position in `frames`. */
export interface IPerfusionScanFrame {
  bucket?: string;
  key?: string;
  region?: string;
  originalName?: string;
  contentType?: string;
  size?: number;
  index?: number;
  [extra: string]: unknown;
}

/** A bound the document printed beside a value: `<` became `<=`, `>` became `>=`. */
export type Inequality = '<=' | '>=';

/** One value a scan read, as spdata-api stores it in `result.fields`. */
export interface ScannedValue {
  /** The label printed on the document. */
  label: string;
  value: string;
  unit?: string;
  /** The form variable the value matched, absent when it matched none. */
  variable?: string;
  inequality?: Inequality;
}

/** What the reader made of the frames. Neither key is certain on a Companion scan. */
export interface PerfusionScanResult {
  documentType?: string;
  fields?: ScannedValue[];
  [key: string]: unknown;
}

/** A user a scan names: their hex id, and their name while they still have one. */
export interface PerfusionScanUser {
  id: string;
  name: string | null;
}

/** One entry a scan was applied to. */
export interface PerfusionScanApplication {
  entryId: string;
  formId: string;
  /** Read from the form as it stands, `null` once the form is gone. */
  formTitle: string | null;
  /** The entry's timepoint as it stands now, `null` when it has none. */
  timepoint: string | null;
  createdBy: PerfusionScanUser | null;
  createdAt: string;
}

export interface IPerfusionScan {
  id: string;
  studyId: string;
  caseId: string;
  pumpCenter?: string | null;
  machineName: string;
  status: string;
  modelId?: string | null;
  result?: PerfusionScanResult | null;
  errorData?: ({ code?: string; message?: string } & Record<string, unknown>) | null;
  frames?: IPerfusionScanFrame[] | null;
  extras?: Record<string, unknown> | null;
  capturedAt?: string | null;
  /** `null` on a Companion scan stored before scans recorded their creator. */
  createdBy?: PerfusionScanUser | null;
  archivedAt?: string | null;
  /** Oldest first. */
  applications?: PerfusionScanApplication[] | null;
  createdAt: string;
  updatedAt: string;
}

export interface IPerfusionScansResponse {
  scans: IPerfusionScan[];
  total: number;
  limit: number;
  offset: number;
}

/** Mirrors spdata-api's own page size, so a case with more scans than this pages explicitly. */
export const SCAN_PAGE_SIZE = 50;

/** The largest page spdata-api sends, whatever the request asks for. */
export const SCAN_PAGE_SIZE_MAX = 200;

export const PerfusionScanStatus = {
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED'
} as const;

/** One rendered row of a scan's collected values. */
export interface PerfusionScanValue {
  /** Human-readable label, e.g. `portalFlow` → "Portal flow". */
  label: string;
  /** Path into the source object, used as the row key. */
  path: string;
  value: string;
}

const BOUND_SYMBOLS: Record<Inequality, string> = { '<=': '≤', '>=': '≥' };

/** A value as the document printed it: its bound, the number and its unit. */
export const printedValue = ({ value, unit, inequality }: ScannedValue): string =>
  [inequality && BOUND_SYMBOLS[inequality], value, unit].filter(Boolean).join(' ');

/** The well-formed entries of `result.fields`, or `undefined` when the result holds no such list. */
const scannedValuesOf = (result: PerfusionScanResult | undefined): ScannedValue[] | undefined => {
  const fields = result?.fields;
  if (!Array.isArray(fields)) {
    return undefined;
  }

  return fields.filter((field): field is ScannedValue =>
    typeof field?.label === 'string' && typeof field.value === 'string'
  );
};

/** Nested readings deeper than this are shown as raw JSON rather than expanded further. */
const MAX_VALUE_DEPTH = 4;

const EMPTY_VALUE = '—';

/** All-caps words (and bare indices) are left alone so acronyms like `PH` survive. */
const isAcronym = (word: string) => word === word.toUpperCase();

export const humanizeScanKey = (key: string): string => {
  const words = key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim()
    .split(/\s+/)
    .filter(word => word.length > 0);

  if (words.length === 0) {
    return key;
  }

  return words
    .map((word, index) => {
      if (isAcronym(word)) {
        return word;
      }

      return index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word.toLowerCase();
    })
    .join(' ');
};

export const formatScanValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return EMPTY_VALUE;
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? `${value}` : EMPTY_VALUE;
  }

  if (typeof value === 'string') {
    return value.trim() || EMPTY_VALUE;
  }

  if (Array.isArray(value)) {
    const items = value.map(item => formatScanValue(item)).filter(item => item !== EMPTY_VALUE);
    return items.length > 0 ? items.join(', ') : EMPTY_VALUE;
  }

  return JSON.stringify(value);
};

/** True for anything rendered as a single row: scalars and arrays that hold only scalars. */
const isLeafValue = (value: unknown): boolean => {
  if (value === null || value === undefined || typeof value !== 'object') {
    return true;
  }

  return (
    Array.isArray(value) &&
    value.every(item => item === null || item === undefined || typeof item !== 'object')
  );
};

/**
 * Turns a scan's free-form jsonb into label/value rows, flattening nested objects into
 * `Parent › Child` labels so an unknown payload still reads as a list of readings.
 */
export const flattenScanValues = (source: unknown): PerfusionScanValue[] => {
  if (source === null || source === undefined) {
    return [];
  }

  if (typeof source === 'object' && Object.keys(source as object).length === 0) {
    return [];
  }

  const rows: PerfusionScanValue[] = [];

  const walk = (value: unknown, label: string, path: string, depth: number) => {
    if (isLeafValue(value) || depth >= MAX_VALUE_DEPTH) {
      rows.push({
        label: label || 'Value',
        path: path || 'value',
        value: formatScanValue(value)
      });
      return;
    }

    const entries: [string, unknown][] = Array.isArray(value) ?
      value.map((item, index) => [`${index + 1}`, item]) :
      Object.entries(value as Record<string, unknown>);

    if (entries.length === 0) {
      rows.push({ label: label || 'Value', path: path || 'value', value: EMPTY_VALUE });
      return;
    }

    for (const [key, child] of entries) {
      const childLabel = humanizeScanKey(key);
      walk(
        child,
        label ? `${label} › ${childLabel}` : childLabel,
        path ? `${path}.${key}` : key,
        depth + 1
      );
    }
  };

  walk(source, '', '', 0);
  return rows;
};

export class PerfusionScanModel {
  id: string;
  studyId: string;
  caseId: string;
  pumpCenter?: string;
  machineName: string;
  status: string;
  modelId?: string;
  result?: PerfusionScanResult;
  errorData?: Record<string, unknown>;
  frames: IPerfusionScanFrame[];
  extras?: Record<string, unknown>;
  capturedAt?: Date;
  createdBy?: PerfusionScanUser;
  archivedAt?: Date;
  applications: PerfusionScanApplication[];
  createdAt: Date;
  updatedAt: Date;
  /** The values the scan read, or `undefined` on a Companion scan, whose result holds no list. */
  private readonly fields?: ScannedValue[];

  constructor(scan: IPerfusionScan) {
    this.id = scan.id;
    this.studyId = scan.studyId;
    this.caseId = scan.caseId;
    this.pumpCenter = scan.pumpCenter ?? undefined;
    this.machineName = scan.machineName;
    this.status = scan.status;
    this.modelId = scan.modelId ?? undefined;
    this.result = scan.result ?? undefined;
    this.errorData = scan.errorData ?? undefined;
    this.frames = scan.frames ?? [];
    this.extras = scan.extras ?? undefined;
    this.capturedAt = scan.capturedAt ? new Date(scan.capturedAt) : undefined;
    this.createdBy = scan.createdBy ?? undefined;
    this.archivedAt = scan.archivedAt ? new Date(scan.archivedAt) : undefined;
    this.applications = scan.applications ?? [];
    this.createdAt = new Date(scan.createdAt);
    this.updatedAt = new Date(scan.updatedAt);
    this.fields = scannedValuesOf(this.result);
  }

  static mapFromBackend(scan: IPerfusionScan): PerfusionScanModel {
    return new PerfusionScanModel(scan);
  }

  get isFailed(): boolean {
    return this.status === PerfusionScanStatus.FAILED;
  }

  /** A scan from the Perfusion Companion app: its result is free-form, so it is shown, never applied. */
  get isCompanionScan(): boolean {
    return this.fields === undefined && !this.isFailed;
  }

  /**
   * What the scan's values are, which decides how they are shown: none, with a reason; a Companion
   * scan's free-form payload; or scanned values, matched to form variables or not.
   */
  get reading(): 'failed' | 'companion' | 'scanned' {
    if (this.isFailed) return 'failed';
    return this.isCompanionScan ? 'companion' : 'scanned';
  }

  /** When the reading was taken, falling back to when the server stored it. */
  get recordedAt(): Date {
    return this.capturedAt ?? this.createdAt;
  }

  get scannedValues(): ScannedValue[] {
    return this.fields ?? [];
  }

  /** The scanned values that matched a form variable: the ones applying the scan can write. */
  get matchedValues(): ScannedValue[] {
    return this.scannedValues.filter(value => !!value.variable);
  }

  get unmatchedValues(): ScannedValue[] {
    return this.scannedValues.filter(value => !value.variable);
  }

  /** Whether applying this scan could write anything. ENG-10010 wires the apply itself. */
  get canApply(): boolean {
    return !this.isFailed && this.matchedValues.length > 0;
  }

  /** Why a failed scan read nothing, when the server said. */
  get failureReason(): string | undefined {
    const message = this.errorData?.message;
    return typeof message === 'string' && message.trim() ? message : undefined;
  }

  /** The scan's values as rows to show: its scanned values, or a Companion scan's payload flattened. */
  get values(): PerfusionScanValue[] {
    if (this.fields === undefined) {
      return flattenScanValues(this.result);
    }

    return this.fields.map((value, index) => ({
      label: value.label,
      path: `fields.${index}`,
      value: printedValue(value)
    }));
  }

  get extraValues(): PerfusionScanValue[] {
    return flattenScanValues(this.extras);
  }

  get errorValues(): PerfusionScanValue[] {
    return flattenScanValues(this.errorData);
  }
}
